import { useState } from "react";
import { Download, Copy, CheckCircle2, Loader2, PlusCircle, Eye } from "lucide-react";
import AssetThumb from "./AssetThumb";
import { copyText } from "@/lib/clipboard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtBytes(n = 0) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

export const BDMAssetCard = ({ asset, authHeader, onLog, logged, onGrabbed, onPreview }) => {
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const download = async () => {
    setBusy(true);
    try {
      const resp = await fetch(`${API}/library/assets/${asset.id}/download`, { headers: authHeader() });
      if (!resp.ok) throw new Error("Download failed");
      const b = await resp.blob();
      const href = URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = href;
      a.download = asset.filename || "asset";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      onGrabbed?.(asset);
    } finally {
      setBusy(false);
    }
  };

  const copyCaption = async () => {
    const ok = await copyText(asset.description || "");
    if (!ok) return;
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <div
      className="rounded-2xl overflow-hidden bg-white border border-linen-border hover:shadow-md transition-shadow"
      data-testid={`bdm-asset-${asset.id}`}
    >
      <button
        type="button"
        onClick={() => onPreview?.(asset)}
        data-testid={`bdm-asset-preview-${asset.id}`}
        aria-label={`Preview ${asset.filename}`}
        className="w-full aspect-[16/10] bg-linen flex items-center justify-center relative overflow-hidden group cursor-zoom-in"
      >
        <AssetThumb asset={asset} authHeader={authHeader} className="w-full h-full" iconSize={34} />
        <span className="absolute inset-0 bg-navy-deep/0 group-hover:bg-navy-deep/35 transition-colors flex items-center justify-center">
          <span className="opacity-0 group-hover:opacity-100 transition-opacity inline-flex items-center gap-1.5 rounded-full bg-white/90 text-navy text-[11px] font-bold px-3 py-1.5">
            <Eye size={12} /> Preview
          </span>
        </span>
        {logged > 0 && (
          <span
            className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest bg-lime text-navy font-semibold px-2 py-1 rounded-md"
            data-testid={`bdm-asset-logged-${asset.id}`}
          >
            <CheckCircle2 className="w-3 h-3" /> Used {logged > 1 ? `${logged}×` : ""}
          </span>
        )}
      </button>
      <div className="p-4">
        <p className="text-sm font-semibold text-navy truncate" title={asset.filename}>
          {asset.filename}
        </p>
        {asset.description ? (
          <p className="text-xs text-ink-500 mt-1 line-clamp-3">{asset.description}</p>
        ) : null}
        <div className="text-[11px] text-ink-400 mt-2">{fmtBytes(asset.size)}</div>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={download}
            disabled={busy}
            data-testid={`bdm-asset-download-${asset.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-navy text-white hover:bg-[#173a5f] disabled:opacity-60"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            Download
          </button>
          {asset.description ? (
            <button
              type="button"
              onClick={copyCaption}
              data-testid={`bdm-asset-caption-${asset.id}`}
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-linen text-navy hover:bg-linen-border"
            >
              <Copy className="w-3 h-3" /> {copied ? "Copied" : "Copy caption"}
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => onLog(asset)}
            data-testid={`bdm-asset-log-${asset.id}`}
            className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-lime text-navy hover:brightness-95"
          >
            <PlusCircle className="w-3 h-3" /> Log this
          </button>
        </div>
      </div>
    </div>
  );
};

export default BDMAssetCard;
